import React, { useEffect, useState } from "react";
import { fireStore } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import "react-datepicker/dist/react-datepicker.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import Cookies from "js-cookie";
import { BiEdit, BiEditAlt } from "react-icons/bi";
import { CgEditBlackPoint } from "react-icons/cg";

const Expenses = () => {
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1); // First day of the current month
  firstDayOfMonth.setHours(0, 0, 0, 1); // Reset time to start of the day

  const [startDate, setStartDate] = useState(firstDayOfMonth.getTime()); // Store as milliseconds
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    now.setHours(23, 59, 59, 999); // Set to the last millisecond of the day
    return now.getTime(); // Store as milliseconds
  });

  const [expenses, setExpenses] = useState([]);
  const [savedExpenses, setSavedExpenses] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [show, setShow] = useState(false);

  const [selectedExpense, setSelectedExpense] = useState(null);
  const [newTotalExpense, setNewTotalExpense] = useState("");
  const [newComments, setNewComments] = useState("");

  const [showApproved, setShowApproved] = useState(false);
  const [territories, setTerritories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTerritory, setSelectedTerritory] = useState("");

  //Fetch Territories..
  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const collectionRef = collection(fireStore, "Territories");
        const querySnapshot = await getDocs(collectionRef);
        const territoriesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort territories in ascending order based on a property, e.g., 'name'
        const sortedTerritories = territoriesList.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        setTerritories(sortedTerritories);
      } catch (error) {
        alert("Error fetching territories");
      }
    };

    fetchTerritories();
  }, []);

  // show expense item..
  const handleShow = (item) => {
    setSelectedExpense(item);
    setNewTotalExpense(item.totalExpense);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setSelectedExpense(null);
    setNewTotalExpense("");
  };

  // user roles..
  useEffect(() => {
    // Get the user role from the cookie
    const role = Cookies.get("userRole");
    setUserRole(role);

    // Optionally, you can handle cases where the role is not found
    if (!role) {
      console.warn("User role not found.");
    }
  }, []);

  // Save document..
  const handleSave = async () => {
    if (selectedExpense && newTotalExpense) {
      // Convert the id to a string
      const expenseIdAsString = selectedExpense.id.toString();

      // Optimistically update the local state before making the Firestore call
      if (userRole === "HSM") {
        setExpenses((prevExpenses) =>
          prevExpenses.map((expense) =>
            expense.id === expenseIdAsString
              ? {
                  ...expense,
                  HSMExpense: newTotalExpense,
                  HSMComments: newComments,
                }
              : expense
          )
        );
      } else {
        setExpenses((prevExpenses) =>
          prevExpenses.map((expense) =>
            expense.id === expenseIdAsString
              ? {
                  ...expense,
                  ADMINExpense: newTotalExpense,
                  ADMINComments: newComments,
                }
              : expense
          )
        );
      }

      try {
        // Get the document reference using the stringified id
        const docRef = doc(fireStore, "MExpenses", expenseIdAsString);

        // Update the document in Firestore
        if (userRole === "HSM") {
          await updateDoc(docRef, {
            HSMExpense: newTotalExpense,
            HSMComments: newComments,
          });
        } else {
          await updateDoc(docRef, {
            ADMINExpense: newTotalExpense,
            ADMINComments: newComments,
          });
        }

        // alert('Update successful');
        handleClose();
      } catch (error) {
        console.error("Error updating document:", error);
        alert("Failed to update. Please try again.");

        // Revert to previous state if Firestore update fails
        setExpenses((prevExpenses) =>
          prevExpenses.map((expense) =>
            expense.id === expenseIdAsString
              ? { ...expense, totalExpense: selectedExpense.totalExpense }
              : expense
          )
        );
      }
    }
  };

  // Fetch Monthly Expenses..
  useEffect(() => {
    async function getMonthyExpenses() {
      const collectionRef = collection(fireStore, "MExpenses");

      const q = query(
        collectionRef,
        where("id", ">=", startDate),
        where("id", "<=", endDate)
      );

      const snapshots = await getDocs(q);

      // Extract data + include doc.id if you want it
      const items = snapshots.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by id (assuming id is numeric)
      const sortedItems = items.sort((a, b) => Number(a.id) - Number(b.id));

      setSavedExpenses(sortedItems);
      setExpenses(sortedItems);
    }

    getMonthyExpenses();
  }, [startDate, endDate]);

  // Verify Click..
  const handleVerifyClick = async (expenseId) => {
    try {
      // Convert ID to string
      const expenseIdAsString = String(expenseId);

      // Get the document reference
      const expenseRef = doc(fireStore, "MExpenses", expenseIdAsString);

      // Determine the new status based on userRole
      let newStatus = "";
      if (userRole === "ADMIN") {
        newStatus = "POSTED";
      } else if (userRole === "HSM") {
        newStatus = "APPROVED";
      } else {
        console.warn("User role not authorized to change status");
        return; // Exit if the user role is not allowed to change status
      }

      // Update the status in Firestore
      await updateDoc(expenseRef, {
        status: newStatus,
      });

      console.log(
        `Expense ${expenseIdAsString} status changed to ${newStatus} successfully`
      );
    } catch (error) {
      console.error("Error changing expense status:", error);
    }
  };

  // Export to PDF..
  const exportToPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 0;

    for (const expense of expenses) {
      const ExpenseElement = document.getElementById(`Expense-${expense.id}`);
      if (!ExpenseElement) continue;

      const canvas = await html2canvas(ExpenseElement, {
        scale: 1.5,
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.6); // JPEG smaller than PNG
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (yPosition + imgHeight > pageHeight) {
        doc.addPage();
        yPosition = 0;
      }

      doc.addImage(imgData, "JPEG", 0, yPosition, imgWidth, imgHeight);
      yPosition += imgHeight;
    }

    doc.save("Expenses.pdf");
  };

  // helpers
  function formatDateForInput(longDate) {
    if (!longDate) return "";
    const d = new Date(longDate);
    return d.toISOString().split("T")[0]; // "2025-08-20"
  }

  function formatDateForDisplay(longDate) {
    if (!longDate) return "";
    const d = new Date(longDate);
    return d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  }

  // 🔍 Filter by selectedTerritory + searchQuery
  useEffect(() => {
    let filtered = savedExpenses;
    if (selectedTerritory && selectedTerritory.toLowerCase() !== "all") {
      filtered = filtered?.filter(
        (item) =>
          item?.user?.territory?.name?.trim().toLowerCase() ===
          selectedTerritory.trim().toLowerCase()
      );
    }

    // Search filter (user name)
    if (searchQuery && searchQuery.trim() !== "") {
      filtered = filtered?.filter((item) =>
        item?.user?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // ✅ Approved filter
    if (showApproved) {
      filtered = filtered?.filter((item) => item?.status === "POSTED");
    }

    setExpenses(filtered);
  }, [selectedTerritory, searchQuery, showApproved, savedExpenses]);

  return (
    <div className="p-4 bg-lightgray h-full overflow-y-scroll">
      <div className="bg-white px-4 py-2 rounded-lg shadow-lg max-w-5xl mx-auto mt-2 lg:sticky top-0 z-50">
        <div className="flex flex-wrap justify-between items-center py-2">
          <h1 className="text-lg font-semibold text-liquidblue">Expenses</h1>

          <button
            onClick={exportToPDF}
            className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-3 py-1"
          >
            PDF
          </button>
        </div>
        <div className="flex flex-wrap justify-between items-center mb-2 space-y-2 md:space-y-0">
          <select
            id="territories"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            onChange={(e) => {
              setSelectedTerritory(e.target.value);
            }}
            value={selectedTerritory}
          >
            <option value="all">Select Territory</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.name}>
                {territory.name}
              </option>
            ))}
          </select>

          {userRole === "CEO" ||
            (userRole === "ADMIN" && (
              <div className="flex gap-2">
                <p>Show Posted</p>
                <input
                  type="checkbox"
                  checked={showApproved}
                  onClick={() => {
                    setShowApproved(!showApproved);
                  }}
                ></input>
              </div>
            ))}

          <div className="flex items-center">
            {/* From Date Picker */}
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              value={formatDateForInput(startDate)} // long -> "2025-08-20"
              onChange={(e) => setStartDate(new Date(e.target.value).getTime())} // back to long
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center mb-4 space-y-2 md:space-y-0">
          <input
            type="text"
            placeholder="Search"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex items-center">
            {/* From Date Picker */}
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              To Date
            </label>
            <input
              type="date"
              value={formatDateForInput(endDate)} // long -> "2025-08-20"
              onChange={(e) => setEndDate(new Date(e.target.value).getTime())} // back to long
            />
          </div>
        </div>
      </div>
      {expenses?.map((item) => (
        <div
          id={`Expense-${item.id}`}
          key={item.id}
          className="bg-white p-6 rounded-lg shadow-lg max-w-5xl mx-auto mt-4"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border-collapse">
              <thead>
                <tr className="flex justify-around">
                  <th className="text-darkteal py-2 px-4 border-b border-gray-200 text-left whitespace-nowrap">
                    {item.user?.name}
                    <br />
                    <p className="text-gray-400">{item.user?.phone}</p>
                  </th>
                  <th className="text-darkteal py-2 px-4 border-b border-gray-200 text-left whitespace-nowrap">
                    {new Date(item.id).toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    })}
                  </th>

                  <th className="text-darkteal py-2 px-4 border-b border-gray-200 text-left whitespace-nowrap">
                    {new Date(item.id).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.territory?.name ? (
                      <h1
                        onClick={() =>
                          setSelectedTerritory(item.user.territory.name)
                        }
                        className="cursor-pointer border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-4 py-1"
                      >
                        {item.user.territory.name}
                      </h1>
                    ) : null}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr key={item.id} className="flex flex-row w-full">
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-10">
                      <div className="flex items-center justify-between">
                        <span className="font-lato">Status</span>
                        <p className="break-words font-lato font-bold">
                          "{item.status}"
                        </p>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">Engine oil</span>
                        <span className="font-lato font-bold">
                          {item.engineOil}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="font-lato">Car Wash</span>
                        <span className="font-lato font-bold">
                          {item.carWash}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">Repair</span>
                        <span className="font-lato font-bold">
                          {item.repair}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">Replacement</span>
                        <span className="font-lato font-bold">
                          {item.replacement}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">toll tax</span>
                        <span className="font-lato font-bold">
                          {item.tollTax}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="font-lato">Air Filter</span>
                        <span className="font-lato font-bold">
                          {item.airFilter}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">Oil Filter</span>
                        <span className="font-lato font-bold">
                          {item.oilFilter}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">Others</span>
                        <span className="font-lato font-bold">
                          {item.others}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-10">
                      <div className="flex items-center justify-between">
                        <span className="font-lato">Drive</span>
                        <span className="font-lato font-bold">
                          {item.drive}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">Fuel Quantity</span>
                        <span className="font-lato font-bold">
                          {item.fuel.toFixed(2)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">Fuel Expense</span>
                        <span className="font-lato font-bold">
                          {item.fuelExp}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">Fuel Average</span>
                        <span className="font-lato font-bold">
                          {item.fuelAve.toFixed(2)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">Personal Travel Kms</span>
                        <span className="font-lato font-bold">
                          {item.personalTravelKms}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">
                          Personal Travel Expense
                        </span>
                        <span className="font-lato font-bold">
                          {item.personalExpense}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="font-lato">Fm Expense</span>
                        <span className="font-lato font-bold">
                          {item.fmExpense}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="font-lato">freight</span>
                        <span className="font-lato font-bold">
                          {item.freight}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                          <span className="font-lato">Claimed Expenses</span>
                          {userRole !== "CEO" && (
                            <button onClick={() => handleShow(item)}>
                              <BiEditAlt />
                            </button>
                          )}
                        </div>
                        <span className="font-lato font-bold">
                          {item.totalExpense}
                        </span>
                      </div>

                      <div
                        className={`fixed inset-0 flex items-center justify-center ${
                          show ? "block" : "hidden"
                        } bg-opacity-50 p-12`}
                        onClick={handleClose}
                      >
                        <div
                          className="bg-white p-6 rounded shadow-md"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <h2 className="text-xl mb-4 font-bold text-liquidblue">
                            Total Expense
                          </h2>
                          <div className="flex flex-col justify-around">
                            <input
                              type="number"
                              id="expense"
                              className="bg-gray-100 border  border-gray-300 text-gray-900 text-sm rounded-lg px-4 py-2 w-[400px]"
                              placeholder="Enter Value"
                              value={newTotalExpense}
                              onChange={(e) =>
                                setNewTotalExpense(e.target.value)
                              }
                              required
                            />
                            <h1 className=" font-bold  text-gray-600">
                              Comments
                            </h1>
                            <textarea
                              id="expense"
                              className="bg-gray-50 border h-20 text-start border-gray-300 text-gray-900 text-sm rounded-lg px-4 py-2 w-full resize-none"
                              placeholder="Comments..."
                              value={newComments}
                              onChange={(e) => {
                                setNewComments(e.target.value);
                              }}
                              required
                            ></textarea>
                          </div>
                          <div className="flex items-center justify-around gap-4 mt-8">
                            <button
                              onClick={handleSave}
                              className="bg-darkteal text-white px-14 py-2 rounded"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleClose}
                              className="bg-red-600 text-white px-12 py-2 rounded"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <span className="font-lato font-bold">Remarks</span>
                      </div>
                      <div className="flex items-center">
                        <p className="w-full break-words font-lato font-extrabold">
                          "{item.remarks}"
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
                {/* Div for HSM Edit info... */}
                {item.HSMExpense && (
                  <div>
                    <h1 className="font-bold text-darkteal">HSM</h1>
                    <div className="flex justify-between px-4">
                      <p className="text-sm text-gray-500">
                        " {item.HSMComments} "
                      </p>
                      {item.HSMExpense >= item.totalExpense && (
                        <p className="font-lato font-bold text-green-600">
                          {item.HSMExpense}
                        </p>
                      )}
                      {item.HSMExpense < item.totalExpense && (
                        <p className="font-lato font-bold text-red-600">
                          {item.HSMExpense}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {/* Div for Admin Edit info... */}
                {item.ADMINExpense && (
                  <div>
                    <h1 className="font-bold text-darkteal">ADMIN</h1>
                    <div className="flex justify-between px-4">
                      <p className="text-sm text-gray-500">
                        " {item.ADMINComments} "
                      </p>
                      {item.ADMINExpense >= item.totalExpense && (
                        <p className="font-lato font-bold text-green-600">
                          {item.ADMINExpense}
                        </p>
                      )}
                      {item.ADMINExpense < item.totalExpense && (
                        <p className="font-lato font-bold text-red-600">
                          {item.ADMINExpense}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {userRole !== "ZBM" && (
                  <div className="flex justify-end p-2">
                    <h1
                      className={`border border-darkteal font-bold transition duration-300 ease-in-out px-4 py-1 cursor-pointer ${
                        item.status === "PENDING"
                          ? "bg-green-500 text-white cursor-default"
                          : "bg-transparent text-darkteal hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md"
                      }`}
                      onClick={
                        item.status !== "PENDING"
                          ? () => handleVerifyClick(item.id)
                          : null
                      }
                    >
                      {item.status === "VERIFIED"
                        ? "APPROVE" //
                        : item.status === "APPROVED"
                        ? "POST" //
                        : item.status === "POSTED"
                        ? "POSTED BY ADMIN"
                        : "WAITING FOR APPROVAL"}
                    </h1>
                  </div>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Expenses;
